import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition, Popover } from '@headlessui/react';
import { XMarkIcon, EyeIcon, EyeSlashIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import useAppStore from '../../stores/useAppStore';
import { authApi, handleApiError } from '../../services/api';
import { registrationService } from '../../services/api/registrationService';
import { generateTenantId, createDisplayNameFromEmail } from '../../utils/tenantUtils';
import { authLogger } from '../../utils/logger';

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

export default function RegistrationModal(): JSX.Element {
  const navigate = useNavigate();
  const { 
    isRegistrationModalOpen, 
    closeRegistrationModal,
    openLoginModal,
    openEmailVerificationModal,
    setRegistrationData,
    setPendingEmailVerification,
    setShowExistingUserMessage,
    setPrefillEmail,
    setNavigationHistory,
    navigationHistory,
    clearNavigationHistory
  } = useAppStore();

  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  // Function to handle navigation to legal pages
  const handleLegalPageNavigation = (e: React.MouseEvent, type: 'terms' | 'privacy') => {
    e.preventDefault();
    e.stopPropagation();
    
    // Save current form data
    setNavigationHistory({
      previousRoute: '/',
      shouldShowBackButton: true,
      savedFormData: {
        ...formData,
        modalType: 'registration'
      }
    });
    
    // Close the modal first
    closeRegistrationModal();
    
    // Navigate to legal page
    const targetPath = type === 'terms' ? '/terms' : '/privacy';
    navigate(targetPath);
  };

  // Restore form data when modal opens if there's saved data
  useEffect(() => {
    if (isRegistrationModalOpen && navigationHistory.savedFormData?.modalType === 'registration') {
      const savedData = navigationHistory.savedFormData;
      setFormData({
        email: savedData.email || '',
        password: savedData.password || '',
        confirmPassword: savedData.confirmPassword || '',
        agreeToTerms: savedData.agreeToTerms || false
      });
      
      // Clear navigation history after restoring data
      clearNavigationHistory();
    }
  }, [isRegistrationModalOpen, navigationHistory.savedFormData, clearNavigationHistory]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.email) {
      setError('Email is required');
      return false;
    }
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    // Check password requirements
    const passwordRequirements = {
      length: formData.password.length >= 8,
      uppercase: /[A-Z]/.test(formData.password),
      lowercase: /[a-z]/.test(formData.password),
      number: /[0-9]/.test(formData.password),
      special: /[!@#$%^&*]/.test(formData.password)
    };

    const failedRequirements = [];
    if (!passwordRequirements.length) failedRequirements.push('be at least 8 characters long');
    if (!passwordRequirements.uppercase) failedRequirements.push('include an uppercase letter (A-Z)');
    if (!passwordRequirements.lowercase) failedRequirements.push('include a lowercase letter (a-z)');
    if (!passwordRequirements.number) failedRequirements.push('include a number (0-9)');
    if (!passwordRequirements.special) failedRequirements.push('include a special character (!@#$%^&*)');

    if (failedRequirements.length > 0) {
      setError(
        'Your password must:\n' +
        failedRequirements.map(req => `• ${req}`).join('\n')
      );
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.agreeToTerms) {
      setError('Please agree to the terms and conditions');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      // Generate a random tenant ID
      const tempTenantId = generateTenantId(8);
      
      // Create display name from email
      const displayName = createDisplayNameFromEmail(formData.email);
      
      // Use N/A for names since we don't collect them in this form
      const firstName = 'N/A';
      const lastName = 'N/A';

      // Prepare registration data for Cognito
      const registrationData = {
        email: formData.email,
        given_name: firstName,
        family_name: lastName,
        'custom:tenantId': tempTenantId,
        'custom:subscriptionTier': 'FREE',
        password: formData.password,
      };

      authLogger.info('Registration attempt', { email: formData.email });
      
      // Phase 1: Create user account and trigger email verification
      const result = await registrationService.signupUser(registrationData);
      
      if (result.success) {
        // Store registration data for phase 2
        const regData = {
          userId: result.userId,
          tenantId: result.tenantId || tempTenantId,
          email: formData.email,
          tenantName: displayName,
          subscriptionTier: 'FREE'
        };
        
        setRegistrationData(regData);
        
        // Temporarily store password for auto-login after email verification
        sessionStorage.setItem('temp_registration_password', formData.password);
        
        setPendingEmailVerification(true);

        toast.success('Account created! Please check your email to verify your account.');
        
        // Close registration modal and open email verification modal
        closeRegistrationModal();
        openEmailVerificationModal();
      }
      
    } catch (err) {
      const errorMessage = handleApiError(err);
      
      authLogger.info('Checking if user already exists:', { errorMessage });
      
      // Check if user already exists
      const isExistingUser = (
        errorMessage.includes('already exists') || 
        errorMessage.includes('UsernameExistsException') ||
        errorMessage.includes('User already exists') ||
        errorMessage.toLowerCase().includes('username exists')
      );
      
      // Check for password policy errors
      const isPasswordPolicyError = (
        errorMessage.includes('InvalidPasswordException') ||
        errorMessage.includes('Password did not conform with policy')
      );
      
      if (isExistingUser) {
        authLogger.info('User already exists, redirecting to login');
        
        // Show info message that account exists and they should login
        toast.error('An account with this email already exists. Please log in instead.');
        
        // Set flag to show existing user message in login modal
        setShowExistingUserMessage(true);
        
        // Prefill the email in the login modal
        setPrefillEmail(formData.email);
        
        // Close registration modal and open login modal
        closeRegistrationModal();
        openLoginModal();
        return;
      }

      if (isPasswordPolicyError) {
        const passwordRequirements = [
          'At least 8 characters long',
          'At least one uppercase letter (A-Z)',
          'At least one lowercase letter (a-z)',
          'At least one number (0-9)',
          'At least one special character (!@#$%^&*)'
        ];

        setError(
          'Your password must meet the following requirements:\n' +
          passwordRequirements.map(req => `• ${req}`).join('\n')
        );
        toast.error('Please check the password requirements below.');
        return;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      authLogger.error('Registration failed', { 
        email: formData.email, 
        error: errorMessage 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (): void => {
    closeRegistrationModal();
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      agreeToTerms: false
    });
    setError('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSwitchToLogin = (): void => {
    closeRegistrationModal();
    openLoginModal();
  };

  return (
    <Transition appear show={isRegistrationModalOpen} as="div">
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel 
                data-testid="signup-modal"
                className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <Dialog.Title
                      as="h3"
                      className="text-2xl font-bold leading-6 text-gray-900 dark:text-gray-100"
                    >
                      Create Account
                    </Dialog.Title>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Get Started with Fabrik 28
                    </p>
                  </div>
                  <button
                    data-testid="signup-close-button"
                    type="button"
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors duration-200"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      data-testid="signup-email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <div className="flex items-center gap-1 mb-2">
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Password
                      </label>
                      <Popover className="relative">
                        {({ open }) => (
                          <>
                            <Popover.Button 
                              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-full p-0.5 -mt-0.5"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.currentTarget.click();
                                }
                              }}
                            >
                              <InformationCircleIcon 
                                className="h-4 w-4" 
                                aria-hidden="true"
                              />
                              <span className="sr-only">Password requirements</span>
                            </Popover.Button>
                            <Transition
                              show={open}
                              as={Fragment}
                              enter="transition ease-out duration-200"
                              enterFrom="opacity-0 translate-y-1"
                              enterTo="opacity-100 translate-y-0"
                              leave="transition ease-in duration-150"
                              leaveFrom="opacity-100 translate-y-0"
                              leaveTo="opacity-0 translate-y-1"
                            >
                              <Popover.Panel 
                                className="absolute z-10 left-0 mt-2 w-72 rounded-lg bg-white dark:bg-gray-800 px-4 py-3 shadow-lg ring-1 ring-black ring-opacity-5"
                                role="tooltip"
                                aria-live="polite"
                              >
                                <div className="space-y-2">
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    Password Requirements:
                                  </p>
                                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                    <li>At least 8 characters long</li>
                                    <li>Include an uppercase letter (A-Z)</li>
                                    <li>Include a lowercase letter (a-z)</li>
                                    <li>Include a number (0-9)</li>
                                    <li>Include a special character (!@#$%^&*)</li>
                                  </ul>
                                </div>
                              </Popover.Panel>
                            </Transition>
                          </>
                        )}
                      </Popover>
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        data-testid="signup-password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        data-testid="signup-confirm-password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                        placeholder="Confirm your password"
                        required
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Terms Agreement */}
                  <div className="flex items-start">
                    <input
                      id="agreeToTerms"
                      name="agreeToTerms"
                      type="checkbox"
                      data-testid="signup-terms"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={(e) => handleLegalPageNavigation(e, 'terms')}
                        className="text-primary-600 hover:text-primary-500 underline"
                      >
                        Terms of Service
                      </button>{' '}
                      and{' '}
                      <button
                        type="button"
                        onClick={(e) => handleLegalPageNavigation(e, 'privacy')}
                        className="text-primary-600 hover:text-primary-500 underline"
                      >
                        Privacy Policy
                      </button>
                    </label>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      {error.split('\n').map((line, index) => (
                        <p key={index} className="text-sm text-red-800 dark:text-red-200">
                          {line}
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    data-testid="signup-submit"
                    disabled={isLoading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </button>
                </form>

                {/* Switch to Login */}
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={handleSwitchToLogin}
                      className="text-primary-600 hover:text-primary-500 font-medium"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}